import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { sendVerificationEmail } from "../services/auth";
import "./AuthPage.css";

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const EMAIL_VERIFICATION_BYPASS_IDS = new Set(
  (process.env.REACT_APP_EMAIL_VERIFICATION_BYPASS_IDS || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
);
const isBypassedUser = (uid?: string | null, email?: string | null): boolean =>
  EMAIL_VERIFICATION_BYPASS_IDS.has((uid || "").toLowerCase()) ||
  EMAIL_VERIFICATION_BYPASS_IDS.has((email || "").toLowerCase());

const VerifyEmailPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(60);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      if (user.emailVerified || isBypassedUser(user.uid, user.email)) {
        navigate("/", { replace: true });
        return;
      }
      setEmail(user.email);
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleResend = async () => {
    setError("");
    try {
      if (!auth.currentUser) throw new Error("Not signed in");
      await sendVerificationEmail(auth.currentUser);
      setCooldown(60);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to resend verification email.";
      setError(msg);
    }
  };

  const logoUrl = PUBLIC_URL ? `${PUBLIC_URL}/logo.svg` : "/logo.svg";
  const disabled = cooldown > 0;
  const countdownLabel = cooldown ? `00:${cooldown.toString().padStart(2, "0")}` : "00:00";

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--forgot">
        <img src={logoUrl} alt="Logo" className="auth-logo" />
        <h1 className="auth-title">Verify your email</h1>
        <p className="auth-subtitle auth-subtitle--forgot">
          We sent an email{email ? ` to ${email}` : ""} to verify your email address and activate your account.
          The link will expire in 24 hours.
        </p>
        {error && (
          <p className="auth-field-error" role="alert">
            {error}
          </p>
        )}
        <p className="auth-subtitle auth-subtitle--forgot--text">Not in inbox or spam folder?</p>
        <button
          type="button"
          className="auth-btn"
          onClick={handleResend}
          disabled={disabled}
          style={{ marginBottom: 8 }}
        >
          {disabled ? "Resend link" : "Resend link"}
        </button>
        <p className="auth-bottom auth-bottom--forgot">{countdownLabel}</p>
        <p className="auth-bottom auth-bottom--forgot">
          Back to <Link to="/login" className="auth-link">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPendingPage;

